import React, { useCallback, useEffect } from 'react';
import { fetchListings as requestListings } from '../../../utils/listingsTransport';
import { resolveListingImages } from '../../../utils/listingImage';

// Shared listing-search state used by the listing pickers (Single Listing and
// the Custom Listing container). Owns the listing fetch (through the host's
// transport, see utils/listingsTransport), paging, and result normalization so
// the panels only render the picker UI.

export interface ListingItem {
	id: string;
	title?: string;
	address?: string;
	city?: string;
	stateProvince?: string;
	postalCode?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
	[key: string]: unknown;
}

export interface ActiveView {
	id: 'mylistings' | 'companylistings';
	page: number;
	size: number;
}

export interface ListingFiltersPayload {
	startdate?: string;
	enddate?: string;
	propertytype?: string[];
	pricemin?: string;
	pricemax?: string;
	status?: string[];
}

export interface ListingSearchParams {
	page: number;
	size: number;
	keyword?: string;
	filters?: ListingFiltersPayload;
	sortBy?: string;
	orderDirection?: 'ASC' | 'DESC';
}

const ALL_PROPERTY_TYPE_VALUES = ['residential', 'multifamily', 'commercial', 'land'];

const SORT_FIELD_MAP: Record<string, string> = {
	list_price: 'list_price',
	adddatetime: 'adddatetime',
};

const normalizePriceFilterValue = (value?: string) => {
	if (!value) return '';
	return value.replace(/[^0-9]/g, '');
};

export function useListingSearch() {
	const [listings, setListings] = React.useState<ListingItem[]>([]);
	const [total, setTotal] = React.useState<number>(0);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [searchStarted, setSearchStarted] = React.useState(false);
	const [activeView, setActiveView] = React.useState<ActiveView>({
		id: 'mylistings',
		page: 1,
		size: 10,
	});

	const getListings = useCallback(
		async (params: ListingSearchParams) => {
			setLoading(true);
			try {
				const { page, size, keyword, filters, sortBy, orderDirection } =
					params || { page: 1, size: 10 };
				const reqParams: any = {
					method: 'read',
					CacheData: true,
					Return_Query: false,
					Return_Array: true,
					startpage: page,
					MaxRows: size,
					IsMyTransactions: 'F',
					IsCoordinatorTransactions: 'F',
					ReturnFormat: 'json',
					propertytype: 'residential,multifamily,commercial,land',
					deleteflag: 'n',
					showallproperties: false,
					query: keyword?.trim() ? keyword : '*',
					searchfield: keyword?.trim() ? 'streetaddress,ml_number' : '',
					gridtabid: activeView?.id || 'mylistings',
				};

				if (sortBy && SORT_FIELD_MAP[sortBy]) {
					reqParams.orderby = SORT_FIELD_MAP[sortBy];
					reqParams.orderdirection = (orderDirection || 'DESC').toLowerCase();
				}

				if (filters) {
					reqParams.startdate = filters.startdate ?? '';
					reqParams.enddate = filters.enddate ?? '';
					reqParams.pricemin = normalizePriceFilterValue(filters.pricemin);
					reqParams.pricemax = normalizePriceFilterValue(filters.pricemax);
					reqParams.statuscode = Array.isArray(filters.status)
						? filters.status.join(',')
						: '';

					if (Array.isArray(filters.propertytype)) {
						reqParams.propertytype = filters.propertytype.length
							? filters.propertytype.join(',')
							: ALL_PROPERTY_TYPE_VALUES.join(',');
					}
				}

				const Data = await requestListings({ params: reqParams });
				let results: ListingItem[] = [];
				if (Data?.STATUS === 0 && Array.isArray(Data?.QUERY)) {
					results = Data.QUERY.map((listing: any) => {
						const images = resolveListingImages(listing);

						return {
							// Keep every raw CFC field (zip_code, propertytype, lo_comp, la_name, …) so
							// merge tags whose dictionary translation points at a raw column name —
							// not one of the convenience aliases below — still resolve.
							...listing,
							id: `${listing?.ml_number}|${listing?.mlsid}|${listing?.statuscode}|${listing?.reduceddatetime}`,
							title: listing?.streetaddress,
							address: `${listing?.streetaddress}`,
							city: listing?.city,
							stateProvince: listing?.state,
							postalCode: listing?.zip_code,
							list_price: listing?.list_price,
							mls: listing?.ml_number,
							dateListed:
								listing?.listingDisplayDate ||
								listing?.LISTINGDISPLAYDATE ||
								listing?.AddDateTime ||
								listing?.adddatetime,
							image_data: images,
							beds: listing?.beds || 0,
							baths: listing?.baths || 0,
							sq_foot: listing?.sq_foot || 0,
							status: listing?.statuscode,
							propertyType: listing?.propertytype,
							agentName: listing?.agentname || '',
						};
					});
				}
				const totalCount = Data?.TOTALROWCOUNT;
				setListings(results);
				setTotal(totalCount);
				return { listings: results, total: totalCount };
			} catch (e) {
				setListings([]);
				setTotal(0);
				return { listings: [], total: 0 };
			} finally {
				setLoading(false);
			}
		},
		[activeView],
	);

	// The search grid belongs to the drawer, not the panel, and this hook is
	// mounted by the Listing Wrapper and listing column panels — which mount on
	// every focus change. Loading here meant clicking a wrapper or column fired a
	// listings.cfc read every time, for a drawer that click usually never opens.
	// `beginSearch` is called when the drawer actually becomes visible.
	// Results already loaded for a view, kept for the life of this panel.
	//
	// Switching My Listings <-> Company Listings changes activeView, which
	// rebuilds getListings and re-ran the effect below — so flipping the tab back
	// and forth refetched a list the drawer had already shown. The drawer is
	// open and the data has not changed; serve it from here instead.
	//
	// Only the default page-1, no-keyword, no-filter load is cached, which is
	// exactly what a view switch asks for: the drawer's handleViewChange resets
	// search, filters, sort and page whenever the tab changes. Paging, searching
	// and filtering still go straight to getListings and always hit the network.
	const viewResultsRef = React.useRef(
		new Map<string, { listings: ListingItem[]; total: number }>(),
	);

	useEffect(() => {
		if (!searchStarted) return;

		const viewId = activeView?.id || 'mylistings';
		const cached = viewResultsRef.current.get(viewId);

		if (cached) {
			setListings(cached.listings);
			setTotal(cached.total);
			setLoading(false);
			return;
		}

		void getListings({ ...activeView, keyword: '' }).then(result => {
			if (result) viewResultsRef.current.set(viewId, result);
		});
	}, [searchStarted, activeView, getListings]);

	// Fires on the FIRST open only, and a ref is what makes that true.
	//
	// setSearchStarted(true) a second time is a no-op, so the effect above does
	// not re-run — but setLoading(true) is not a no-op, and with no fetch to
	// clear it the drawer sat on its skeleton forever. That happened on any
	// reopen, and in dev on the very first open, since StrictMode invokes the
	// drawer's visible-effect twice.
	//
	// Skipping the repeat is also the behaviour we want: the drawer keeps the
	// rows it already loaded instead of refetching them every time it is opened.
	//
	// setLoading lives here rather than as the initial state because `loading`
	// also drives the attached-listing summary row OUTSIDE the drawer — starting
	// it true left that row spinning on any block whose drawer was never opened.
	const hasBegunRef = React.useRef(false);
	const beginSearch = useCallback(() => {
		if (hasBegunRef.current) return;
		hasBegunRef.current = true;
		setSearchStarted(true);
		setLoading(true);
	}, []);

	const onPaginationChange = useCallback(
		(params: ListingSearchParams) => {
			getListings(params);
		},
		[getListings],
	);

	return {
		listings,
		total,
		loading,
		beginSearch,
		activeView,
		setActiveView,
		getListings,
		onPaginationChange,
	};
}
